/**
 * Rasteriza a página 1 de um PDF em PNG, no browser.
 *
 * O `sharp` do backend NÃO abre PDF — o binário pré-compilado do libvips vem
 * sem poppler/pdfium (questão de licença), então não é config, é recompilar.
 * Fazer aqui evita infra nova e, principalmente, mostra a logo renderizada
 * ANTES de subir: é assim que se descobre que o "PDF da logo" é um manual de
 * marca de 40 páginas.
 *
 * O import é dinâmico de propósito — o Vite separa o chunk e só quem abre a
 * tela baixa o pdf.js.
 */

const MAX_EDGE = 2048;

export interface RasterizeResult {
  blob: Blob;
  width: number;
  height: number;
  pageCount: number;
}

export const rasterizePdf = async (file: File): Promise<RasterizeResult | null> => {
  try {
    const pdfjs = await import('pdfjs-dist');
    const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

    const buffer = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: buffer }).promise;
    const page = await doc.getPage(1);

    // Escala para caber em MAX_EDGE: logo vetorial rasterizada em 96dpi sai
    // pequena demais para virar ícone depois.
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(MAX_EDGE / Math.max(base.width, base.height), 4);
    const viewport = page.getViewport({ scale: Math.max(scale, 1) });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);

    // pdf.js 6 recebe o próprio canvas; `canvasContext` está depreciado.
    await page.render({ canvas, viewport }).promise;

    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/png'));
    if (!blob) { return null; }

    return { blob, width: canvas.width, height: canvas.height, pageCount: doc.numPages };
  } catch (error) {
    // Formato que o pdf.js não abre (CDR, EPS binário) não impede o upload:
    // guarda o original e a tela mostra card genérico.
    console.warn('Não foi possível rasterizar o arquivo:', error);
    return null;
  }
};
